import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { NAV_LINKS } from '../utils/constants';
import { debounce } from '../utils/helpers';

/**
 * Navigation Bar Component
 * Handles responsive navigation, scroll detection, and mobile menu toggle.
 * 
 * @param {Object} props - Component props
 * @param {string} props.activeSection - Currently active section ID for highlighting
 * @returns {JSX.Element} Navbar component
 */
const Navbar = ({ activeSection }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);



  useEffect(() => {
    const handleScroll = debounce(() => {
      setScrolled(window.scrollY > 50);
    }, 50);

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
    // Aria handling or body scroll lock could go here
    document.body.style.overflow = !isOpen ? 'hidden' : 'auto';
  };

  const closeMenu = () => {
    setIsOpen(false);
    document.body.style.overflow = 'auto';
  };

  return (
    <nav className={`navbar ${scrolled ? 'scrolled' : ''}`} role="navigation" aria-label="Main Navigation">
      <div className="logo">
        <a href="#home" onClick={closeMenu} aria-label="Go to Homepage">
          <span className="logo-accent">M</span>owli
        </a>
      </div>

      {/* Desktop Menu */}
      <ul className={`nav-links ${isOpen ? 'active' : ''}`}>
        {NAV_LINKS.map((link) => (
          <li key={link.id}>
            <a
              href={link.href}
              className={activeSection === link.id ? 'active' : ''}
              onClick={closeMenu}
              aria-current={activeSection === link.id ? 'page' : undefined}
            >
              {link.label}
            </a>
          </li>
        ))}
      </ul>

      {/* Mobile Menu Toggle */}
      <button
        className={`mobile-menu-toggle ${isOpen ? 'active' : ''}`}
        onClick={toggleMenu}
        aria-label={isOpen ? "Close menu" : "Open menu"}
        aria-expanded={isOpen}
      >
        {isOpen ? <X size={28} color="white" /> : <Menu size={28} color="white" />}
      </button>

      {/* Mobile Menu Overlay */}
      {isOpen && <div className="menu-overlay" onClick={closeMenu} aria-hidden="true"></div>}
    </nav>
  );
};

export default Navbar;
